/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/
"use client";
import React, {useEffect} from 'react';
import {SubmitHandler} from "react-hook-form";
import {FaAt, FaPaperPlane, FaWhatsapp} from "react-icons/fa6";
import {AppForm, AppInput, AppTextarea} from "../../components/form";
import {boolean, object, string, TypeOf} from "zod";
import {toast} from "react-toastify";
import {AppSwitch} from "../../components/form/AppSwitch";
import {CiCircleCheck} from "react-icons/ci";
import AppSubmit from "../../components/form/AppSubmit";
import {contactApi} from "../../services/contactApi";
import AppStatic from "../../utils/AppStatic";
import {appWappURL, recaptchaSiteKey} from '../../utils/AppConfig';
import {scrollToHeader} from "../../utils/ScrollToHeader";
import {AppRecaptcha} from '@/components/form/AppRecaptcha.tsx';


const contactConfigSchema = object({
    name: string().min(1, {message: AppStatic.mandatoryField.ro.translation}),
    phoneNo: string().min(1, {message: AppStatic.mandatoryField.ro.translation}),
    email: string().min(1, {message: AppStatic.mandatoryField.ro.translation}),
    message: string().min(1, {message: AppStatic.mandatoryField.ro.translation}),
    gdpr: boolean({message: AppStatic.mandatoryField.ro.translation}),
	recaptchaToken: string().optional(),
});

export type ContactConfigInput = TypeOf<typeof contactConfigSchema>;

export type contactProps = {
    minimalContact?: boolean,
    textMainColorClass?: string
    textSecondaryColorClass?: string
    inputBgClass?: string
}

const AppContact = (contactProps?: contactProps) => {
    const [contactSent, setContactSent] = React.useState<boolean>(false);
    const [createContact, {
        isLoading: isLoadingAddContact,
        isSuccess: isSuccessAddContact,
    }] = contactApi.endpoints.createContact.useMutation();

    useEffect(() => {
        if (isSuccessAddContact) {
            scrollToHeader();
            toast.success(AppStatic.contactSentNotification.ro.translation);
            setContactSent(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingAddContact]);

    const onSubmitContactAddHandler: SubmitHandler<ContactConfigInput> = (values: any) => {
        scrollToHeader();
        if (values.gdpr === false) {
            toast.error(AppStatic.mustComplyGDPR.ro.translation);
            return;
        }
		if(!values.recaptchaToken || values.recaptchaToken.length == 0) {
			toast.error(AppStatic.mandatoryRecaptchaField.ro.translation);
			return;
		}
        createContact({request: values});
    };

    return (
        <div id="app-contact"
             className={`${!contactProps?.minimalContact ? `pl-5 pr-5 sm:pl-10 sm:pr-10 pt-32 lg:pl-20 lg:pr-20 bg-white/10 md:pt-40` : `md:mt-10`}  pb-4`}>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                <div>
                    {contactProps?.minimalContact ? (
                        <>
                            <h3 className={`text-4xl ${contactProps.textMainColorClass} font-bold md:text-6xl mb-2`}>
                                <div>
                                    {AppStatic.contactAmbassadorHeaderFirst.ro.translation}
                                </div>
                                <div>
                                    {AppStatic.contactAmbassadorHeaderSecond.ro.translation}
                                </div>
                            </h3>
                            <div
                                className={`text-base font-thin ${contactProps.textMainColorClass} mb-16 md:max-w-[550px]`}>
                                {AppStatic.contactAmbassadorProposition.ro.translation}
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className="text-4xl text-black font-bold md:text-6xl mb-2">
                                {AppStatic.contactHeader.ro.translation}
                            </h1>
                            <div className="text-base mb-16 md:max-w-[550px]">
                                {AppStatic.contactProposition.ro.translation}
                            </div>
                        </>
                    )}

                    <a href={appWappURL} target="_blank" rel="noreferrer"
                       className="flex items-center gap-4 mt-8 hover:scale-105 transition-transform ease-in-out duration-300">
                        <FaWhatsapp className={`text-5xl text-[#25d366] ${contactProps?.textMainColorClass}`}/>
                        <div>
                            <div className={`text-xl text-black ${contactProps?.textMainColorClass}`}>WhatsApp</div>
                            <div className="text-sm text-mono-200">{AppStatic.contactWhatsApp.ro.translation}</div>
                        </div>
                    </a>
                    <a href="mailto:contact@diaphan.ro" target="_blank" rel="noreferrer"
                       className="flex items-center gap-4 lg:mb-16 mt-8 hover:scale-105 transition-transform ease-in-out duration-300">
                        <FaAt className={`text-4xl ${contactProps?.textMainColorClass}`}/>
                        <div>
                            <div className={`text-xl text-black ml-3 ${contactProps?.textMainColorClass}`}>Email</div>
                            <div className="text-sm text-mono-200 ml-3">contact@diaphan.ro</div>
                        </div>
                    </a>
                </div>
                <div
                    className={`relative mt-4 lg:-mt-16! lg:p-10 xl:p-20 rounded-3xl ${!contactProps?.minimalContact ? `app-contact-box-shadow bg-white ` : `bg-[#232323]`}`}>
                    {contactSent && !contactProps?.minimalContact ? (
                        <div className="hidden w-full justify-center lg:flex">
                            <CiCircleCheck
                                className="absolute text-9xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"/>
                        </div>
                    ) : (
                        <AppForm<ContactConfigInput>
                            id="contact-form"
                            onSubmit={onSubmitContactAddHandler}
                            schema={contactConfigSchema}
                        >
                            {({register, formState, setValue}) => (
                                <>
                                    <div className={"grid grid-cols-1 gap-0"}>
                                        <AppInput
                                            className={`text-lg! font-thin bg-white! ${contactProps?.inputBgClass} text-black!`}
                                            label={AppStatic.contactFormName.ro.translation}
                                            error={formState.errors['name']}
                                            registration={register('name')}
                                        />
                                        <AppInput
                                            className={`text-lg! font-thin bg-white! ${contactProps?.inputBgClass} text-black!`}
                                            label={AppStatic.contactPhone.ro.translation}
                                            error={formState.errors['phoneNo']}
                                            registration={register('phoneNo')}
                                        />
                                        <AppInput
                                            className={`text-lg! font-thin bg-white! ${contactProps?.inputBgClass} text-black!`}
                                            label={AppStatic.contactFormEmail.ro.translation}
                                            error={formState.errors['email']}
                                            registration={register('email')}
                                        />
                                        <AppTextarea
                                            className={`text-lg! font-thin bg-white! ${contactProps?.inputBgClass} text-black!`}
                                            label={AppStatic.contactFormMessage.ro.translation}
                                            registration={register('message')}
                                            error={formState.errors['message']}
                                            rows={4}
                                        />
                                        <AppSwitch
                                            label={AppStatic.formGDPRPolicy.ro.translation}
                                            error={formState.errors['gdpr']}
                                            textColor={contactProps?.textSecondaryColorClass}
                                            registration={register('gdpr')}
                                            mt={'mt-3'}
                                            mb={'mb-3'}
                                        />

										<AppRecaptcha
											siteKey={recaptchaSiteKey}
											error={formState.errors['recaptchaToken']}
											registration={register('recaptchaToken')}
											onChange={(token) => setValue('recaptchaToken', token || '')}
											mt="mt-4"
											mb="mb-4"
										/>
                                    </div>
                                    <div className="w-full flex justify-start">
                                        <AppSubmit
                                            extraClass={`w-full! text-black! ${contactProps?.inputBgClass} bg-white! h-12! lg:w-48! mt-6! !initial`}
                                            loadClass={"bg-transparent!"}
                                            icon={<FaPaperPlane/>} type={"submit"}
                                            text={AppStatic.contactFormSubmitText.ro.translation}
                                            isLoading={isLoadingAddContact}/>
                                    </div>
                                </>
                            )}
                        </AppForm>
                    )}
                </div>
            </div>
        </div>
    );
};
export default AppContact;
